/**
 * Pure ImageData helpers for Image Editor Tier 3 retouch tools
 * (clone stamp, toning brushes, linear gradient).
 */

/**
 * Soft circular brush iterator.
 * @param {number} cx
 * @param {number} cy
 * @param {number} brushSize diameter in px
 * @param {number} width
 * @param {number} height
 * @param {(x: number, y: number, falloff: number) => void} fn falloff 0..1
 */
export function forEachBrushPixel(cx, cy, brushSize, width, height, fn) {
  const radius = Math.max(0.5, Number(brushSize) / 2);
  const rCeil = Math.ceil(radius);
  const r2 = radius * radius;
  const x0 = Math.max(0, Math.floor(cx - rCeil));
  const y0 = Math.max(0, Math.floor(cy - rCeil));
  const x1 = Math.min(width - 1, Math.ceil(cx + rCeil));
  const y1 = Math.min(height - 1, Math.ceil(cy + rCeil));
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2) continue;
      const falloff = 1 - Math.sqrt(d2) / radius;
      if (falloff <= 0) continue;
      fn(x, y, falloff);
    }
  }
}

/**
 * @param {ImageData} imageData
 * @param {number} x
 * @param {number} y
 * @returns {{ r: number, g: number, b: number, a: number }}
 */
export function samplePixel(imageData, x, y) {
  const w = imageData.width;
  const h = imageData.height;
  const sx = Math.max(0, Math.min(w - 1, Math.round(x)));
  const sy = Math.max(0, Math.min(h - 1, Math.round(y)));
  const i = (sy * w + sx) * 4;
  const d = imageData.data;
  return { r: d[i], g: d[i + 1], b: d[i + 2], a: d[i + 3] };
}

/**
 * Clone stamp dab: copy from (dest + offset) in source buffer onto dest buffer.
 * Offset is typically (sourceOrigin - strokeStart) so source tracks the brush.
 *
 * @param {ImageData} dest
 * @param {ImageData} sourceBuffer usually a snapshot at stroke start
 * @param {number} destX
 * @param {number} destY
 * @param {{ x: number, y: number }} offset
 * @param {number} brushSize
 * @returns {boolean} true if any pixel written
 */
export function applyCloneStampDab(
  dest,
  sourceBuffer,
  destX,
  destY,
  offset,
  brushSize,
) {
  if (!dest || !sourceBuffer || !offset) return false;
  const w = dest.width;
  const h = dest.height;
  if (sourceBuffer.width !== w || sourceBuffer.height !== h) return false;
  const data = dest.data;
  const src = sourceBuffer.data;
  let dirty = false;
  forEachBrushPixel(destX, destY, brushSize, w, h, (x, y, falloff) => {
    const sx = Math.round(x + offset.x);
    const sy = Math.round(y + offset.y);
    if (sx < 0 || sy < 0 || sx >= w || sy >= h) return;
    const di = (y * w + x) * 4;
    const si = (sy * w + sx) * 4;
    const t = falloff;
    data[di] = Math.round(data[di] * (1 - t) + src[si] * t);
    data[di + 1] = Math.round(data[di + 1] * (1 - t) + src[si + 1] * t);
    data[di + 2] = Math.round(data[di + 2] * (1 - t) + src[si + 2] * t);
    data[di + 3] = Math.round(data[di + 3] * (1 - t) + src[si + 3] * t);
    dirty = true;
  });
  return dirty;
}

/**
 * Sample a box-blurred RGB at (x,y) from buffer (radius 1 = 3x3).
 * @param {Uint8ClampedArray} data
 * @param {number} w
 * @param {number} h
 * @param {number} x
 * @param {number} y
 * @param {number} [kernelRadius]
 */
function sampleBlurred(data, w, h, x, y, kernelRadius = 1) {
  let r = 0;
  let g = 0;
  let b = 0;
  let a = 0;
  let n = 0;
  const kr = Math.max(1, Math.round(kernelRadius));
  for (let oy = -kr; oy <= kr; oy += 1) {
    for (let ox = -kr; ox <= kr; ox += 1) {
      const sx = Math.max(0, Math.min(w - 1, x + ox));
      const sy = Math.max(0, Math.min(h - 1, y + oy));
      const i = (sy * w + sx) * 4;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      a += data[i + 3];
      n += 1;
    }
  }
  return {
    r: r / n,
    g: g / n,
    b: b / n,
    a: a / n,
  };
}

/**
 * @param {number} strength 0..1
 */
function clampStrength(strength) {
  const s = Number(strength);
  if (!Number.isFinite(s)) return 0.35;
  return Math.max(0, Math.min(1, s));
}

/**
 * @param {ImageData} dest
 * @param {ImageData} sourceBuffer
 * @param {number} cx
 * @param {number} cy
 * @param {number} brushSize
 * @param {number} strength
 * @returns {boolean}
 */
export function applyBlurBrushDab(
  dest,
  sourceBuffer,
  cx,
  cy,
  brushSize,
  strength,
) {
  if (!dest || !sourceBuffer) return false;
  const w = dest.width;
  const h = dest.height;
  const amt = clampStrength(strength);
  const data = dest.data;
  const src = sourceBuffer.data;
  let dirty = false;
  forEachBrushPixel(cx, cy, brushSize, w, h, (x, y, falloff) => {
    const blurred = sampleBlurred(src, w, h, x, y, 1);
    const i = (y * w + x) * 4;
    const t = falloff * amt;
    data[i] = Math.round(src[i] * (1 - t) + blurred.r * t);
    data[i + 1] = Math.round(src[i + 1] * (1 - t) + blurred.g * t);
    data[i + 2] = Math.round(src[i + 2] * (1 - t) + blurred.b * t);
    dirty = true;
  });
  return dirty;
}

/**
 * Unsharp-mask style sharpen under the brush.
 * @param {ImageData} dest
 * @param {ImageData} sourceBuffer
 * @param {number} cx
 * @param {number} cy
 * @param {number} brushSize
 * @param {number} strength
 * @returns {boolean}
 */
export function applySharpenBrushDab(
  dest,
  sourceBuffer,
  cx,
  cy,
  brushSize,
  strength,
) {
  if (!dest || !sourceBuffer) return false;
  const w = dest.width;
  const h = dest.height;
  const amt = clampStrength(strength) * 2;
  const data = dest.data;
  const src = sourceBuffer.data;
  let dirty = false;
  forEachBrushPixel(cx, cy, brushSize, w, h, (x, y, falloff) => {
    const blurred = sampleBlurred(src, w, h, x, y, 1);
    const i = (y * w + x) * 4;
    const t = falloff * amt;
    const nr = src[i] + (src[i] - blurred.r) * t;
    const ng = src[i + 1] + (src[i + 1] - blurred.g) * t;
    const nb = src[i + 2] + (src[i + 2] - blurred.b) * t;
    data[i] = Math.max(0, Math.min(255, Math.round(nr)));
    data[i + 1] = Math.max(0, Math.min(255, Math.round(ng)));
    data[i + 2] = Math.max(0, Math.min(255, Math.round(nb)));
    dirty = true;
  });
  return dirty;
}

/**
 * Smudge: smear sourceBuffer colors toward the brush tip along the stroke.
 * @param {ImageData} dest
 * @param {ImageData} sourceBuffer
 * @param {number} cx
 * @param {number} cy
 * @param {number|null} prevX
 * @param {number|null} prevY
 * @param {number} brushSize
 * @param {number} strength
 * @returns {boolean}
 */
export function applySmudgeBrushDab(
  dest,
  sourceBuffer,
  cx,
  cy,
  prevX,
  prevY,
  brushSize,
  strength,
) {
  if (!dest || !sourceBuffer) return false;
  const w = dest.width;
  const h = dest.height;
  const amt = clampStrength(strength);
  const data = dest.data;
  const src = sourceBuffer.data;
  const hasPrev =
    typeof prevX === 'number' &&
    typeof prevY === 'number' &&
    Number.isFinite(prevX) &&
    Number.isFinite(prevY);
  const pullX = hasPrev ? prevX - cx : 0;
  const pullY = hasPrev ? prevY - cy : 0;
  let dirty = false;
  forEachBrushPixel(cx, cy, brushSize, w, h, (x, y, falloff) => {
    const sx = Math.round(x + pullX);
    const sy = Math.round(y + pullY);
    const sample =
      sx >= 0 && sy >= 0 && sx < w && sy < h
        ? (() => {
            const si = (sy * w + sx) * 4;
            return { r: src[si], g: src[si + 1], b: src[si + 2], a: src[si + 3] };
          })()
        : samplePixel(sourceBuffer, x, y);
    const i = (y * w + x) * 4;
    const t = falloff * amt;
    data[i] = Math.round(src[i] * (1 - t) + sample.r * t);
    data[i + 1] = Math.round(src[i + 1] * (1 - t) + sample.g * t);
    data[i + 2] = Math.round(src[i + 2] * (1 - t) + sample.b * t);
    dirty = true;
  });
  return dirty;
}

/**
 * Dodge (lighten) or burn (darken).
 * @param {ImageData} dest
 * @param {number} cx
 * @param {number} cy
 * @param {number} brushSize
 * @param {number} strength
 * @param {'dodge'|'burn'} mode
 * @returns {boolean}
 */
export function applyDodgeBurnBrushDab(
  dest,
  cx,
  cy,
  brushSize,
  strength,
  mode,
) {
  if (!dest) return false;
  const w = dest.width;
  const h = dest.height;
  const amt = clampStrength(strength);
  const data = dest.data;
  const dodge = mode === 'dodge';
  let dirty = false;
  forEachBrushPixel(cx, cy, brushSize, w, h, (x, y, falloff) => {
    const i = (y * w + x) * 4;
    const t = falloff * amt * 0.45;
    if (dodge) {
      data[i] = Math.min(255, Math.round(data[i] + (255 - data[i]) * t));
      data[i + 1] = Math.min(255, Math.round(data[i + 1] + (255 - data[i + 1]) * t));
      data[i + 2] = Math.min(255, Math.round(data[i + 2] + (255 - data[i + 2]) * t));
    } else {
      data[i] = Math.max(0, Math.round(data[i] * (1 - t)));
      data[i + 1] = Math.max(0, Math.round(data[i + 1] * (1 - t)));
      data[i + 2] = Math.max(0, Math.round(data[i + 2] * (1 - t)));
    }
    dirty = true;
  });
  return dirty;
}

/**
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {{ h: number, s: number, l: number }}
 */
function rgbToHsl(r, g, b) {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
  else if (max === gg) h = ((bb - rr) / d + 2) / 6;
  else h = ((rr - gg) / d + 4) / 6;
  return { h, s, l };
}

/**
 * @param {number} h
 * @param {number} s
 * @param {number} l
 * @returns {{ r: number, g: number, b: number }}
 */
function hslToRgb(h, s, l) {
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}

/**
 * Sponge: saturate or desaturate.
 * @param {ImageData} dest
 * @param {number} cx
 * @param {number} cy
 * @param {number} brushSize
 * @param {number} strength
 * @param {'saturate'|'desaturate'} mode
 * @returns {boolean}
 */
export function applySpongeBrushDab(
  dest,
  cx,
  cy,
  brushSize,
  strength,
  mode,
) {
  if (!dest) return false;
  const w = dest.width;
  const h = dest.height;
  const amt = clampStrength(strength);
  const data = dest.data;
  const satDir = mode === 'desaturate' ? -1 : 1;
  let dirty = false;
  forEachBrushPixel(cx, cy, brushSize, w, h, (x, y, falloff) => {
    const i = (y * w + x) * 4;
    const hsl = rgbToHsl(data[i], data[i + 1], data[i + 2]);
    const delta = falloff * amt * 0.5 * satDir;
    const nextS = Math.max(0, Math.min(1, hsl.s + delta));
    const rgb = hslToRgb(hsl.h, nextS, hsl.l);
    data[i] = rgb.r;
    data[i + 1] = rgb.g;
    data[i + 2] = rgb.b;
    dirty = true;
  });
  return dirty;
}

/**
 * Linear gradient FG→BG across the buffer (or inside selection mask).
 * @param {ImageData} imageData
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @param {{ r: number, g: number, b: number }} colorA
 * @param {{ r: number, g: number, b: number }} colorB
 * @param {{ width: number, height: number, mask: Uint8Array }|null|undefined} selection
 * @returns {boolean}
 */
export function applyLinearGradient(
  imageData,
  x0,
  y0,
  x1,
  y1,
  colorA,
  colorB,
  selection,
) {
  if (!imageData) return false;
  const w = imageData.width;
  const h = imageData.height;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-6) return false;
  const data = imageData.data;
  const useSel =
    selection &&
    selection.mask &&
    selection.width === w &&
    selection.height === h;
  let dirty = false;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const idx = y * w + x;
      if (useSel && !selection.mask[idx]) continue;
      const t = Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / len2));
      const i = idx * 4;
      data[i] = Math.round(colorA.r + (colorB.r - colorA.r) * t);
      data[i + 1] = Math.round(colorA.g + (colorB.g - colorA.g) * t);
      data[i + 2] = Math.round(colorA.b + (colorB.b - colorA.b) * t);
      data[i + 3] = 255;
      dirty = true;
    }
  }
  return dirty;
}

/**
 * True if the tool id is a Tier-3 brush that uses stroke + pushHistory on up.
 * @param {string} tool
 * @returns {boolean}
 */
export function isRetouchBrushTool(tool) {
  return (
    tool === 'clone-stamp' ||
    tool === 'blur' ||
    tool === 'sharpen' ||
    tool === 'smudge' ||
    tool === 'dodge' ||
    tool === 'burn' ||
    tool === 'sponge'
  );
}
